import React, { useEffect, useContext, useState, useMemo } from 'react';
import {
  Footer,
  HeaderNavi,
  ScrollTop,
  MyPageLeftMenuStudent,
} from '@components';
import { AuthContext } from '@context/AuthContext';
import { HeaderContext } from '@context/HeaderContext';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {ModalUser} from '../lecture/ModalUser';
import {ApplyCompleteModal} from '../lecture/ApplyCompleteModal';
import { useLectureDetail } from '@hooks';

const MyStudentLectureDetail = () => {
  const { auth } = useContext(AuthContext);
  const { subMenu, setSubMenu } = useContext(HeaderContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { lectureId } = useParams(); 
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDoneModal, setIsDoneModal] = useState(false);


  const location = useLocation(); 
  const subMenuFromState = location.state?.subMenu;

  const { lecture, schedule, applyStatus, isPending, isApproved, isReapply, loading, handleCancelApply, refetchStatus } = useLectureDetail(lectureId);
  

  // 로그인 여부 확인
  useEffect(() => {
    if (subMenuFromState) {
      setSubMenu(subMenuFromState);
    } else {
      setSubMenu('나의 교육정보');
    }
  }, [navigate, setSubMenu, subMenuFromState]);


  const grouped = useMemo(() => {
          if (!Array.isArray(schedule) || schedule.length === 0) return {};
          return schedule.reduce((acc, row) => {
              (acc[row.eduDt] = acc[row.eduDt] || []).push(row);
              return acc;
          }, {});
      }, [schedule]);  
  
  if (loading) return <div>로딩중</div>;
  if (!lecture) return null;

  const lectureNm = 
  lecture.catgRegYear && lecture.eduNo && lecture.lectureNm
    ? `[${lecture.catgRegYear}년 ${lecture.eduNo}차] ${lecture.lectureNm}` : "-";
  const period =
      lecture.applyStartDt && lecture.applyEndDt
      ? `${lecture.applyStartDt} ~ ${lecture.applyEndDt}`
      : "-";
  const lectureDate =
      lecture.firstDt && lecture.lastDt
      ? `${lecture.firstDt} ~ ${lecture.lastDt}`
      : lecture.firstDt || "-";
  const hoursText = lecture.eduHours ? `${lecture.eduHours} 시간` : "-";
  const feeText = lecture.eduPee ? `₩ ${lecture.eduPee.toLocaleString()} ` : "-";
  const addressText = lecture.address1
      ? `${lecture.zipcode ? `(${lecture.zipcode}) ` : ""}${lecture.address1}${
          lecture.address2 ? ` ${lecture.address2}` : ""
      }`
      : "-";

  const handleList = () => {
      navigate('/myLectures/myStudentLectures'); 
  }

  const openModal = () => {
      if (!auth.isAuthenticated) {
          alert('로그인이 필요합니다.');
          return;
      }
      setIsModalOpen(true);
  };

  const handleApplyDone = () => {
      queryClient.invalidateQueries({ queryKey: ["myLecturesStudent"] });
      refetchStatus();
      setIsModalOpen(false);
      setIsDoneModal(true);
  }




  const renderSchedule = (date, list) => {
      // 현재 idx부터 같은 강사가 몇 행 이어지는지 계산
      const getSpan = (start) => {
        let span = 1;
        while (
          start + span < list.length &&
          list[start + span].instructorNm === list[start].instructorNm
        ) {
          span += 1;
        }
        return span;
      };
    
      return (
        <div key={date} style={{ marginTop: 16 }}>
          <span style={{fontSize:"1.3rem", fontWeight:"500"}}>
            {date}
          </span>
    
          <div className="lineTop_tb01"> 
              <table className="lineTop_tb">
              <thead>
                  <tr>
                  <th style={{ width: "8%" }}>교시</th>
                  <th>과목</th>
                  <th style={{ width: "22%" }}>시간</th>
                  <th style={{ width: "22%" }}>강사</th>
                  </tr>
              </thead>
              <tbody>
                  {list.map((c, idx) => {
                  const isFirst =
                      idx === 0 ||
                      list[idx - 1].instructorNm !== c.instructorNm;
                  const span = isFirst ? getSpan(idx) : 0; 
      
                  return (
                      <tr key={`${date}-${c.classNo}`}>
                      <td>{c.classNo}</td>
                      <td>{c.subject}</td>
                      <td>{`${c.startTm.slice(0, 2)}:${c.startTm.slice(2, 4)} ~ ${c.endTm.slice(0, 2)} : ${c.endTm.slice(2, 4)}`}</td>
                      {isFirst && <td rowSpan={span}>{c.instructorNm}</td>}
                      </tr>
                  );
                  })}
              </tbody>
              </table>
          </div>
        </div>
      );
  };

  const statusButtons = () => {
    if (!auth.isAuthenticated) {
      return <button className="btn btn-blue" onClick={handleList}>목록으로</button>;
    }
  
    const status = lecture.lectureStatus;            // A,B,C,D
    const pending   = isPending;                     // 신청상태 A(대기)
    const approved  = isApproved;                    // 신청상태 B(승인)
    const reapply   = isReapply;                     // 신청상태 C·N·R
  
    /* 개강 전 (A) */
    if (status === 'A') {
      if (pending) {
        return (
          <>
            <button className="btn btn_red" onClick={handleCancelApply}>교육 신청 취소</button>
            <button className="btn btn_blue ml10" onClick={() => setIsDoneModal(true)}>교육 신청 확인</button>
            <button className="btn btn-blue ml10" onClick={handleList}>목록으로</button>
          </>
        );
      }
      if (approved) {
        return (
          <>
            <button className="btn btn_blue" onClick={() => setIsDoneModal(true)}>교육 신청 확인</button>
            <button className="btn btn-blue ml10" onClick={handleList}>목록으로</button>
          </>
        );
      }
      /* 재신청(취소·반려) ― 개강 전일 때만 허용 */
      if (reapply) {
        return (
          <>
            <button className="btn btn_red" onClick={openModal}>재신청</button>
            <button className="btn btn_blue ml10" onClick={() => setIsDoneModal(true)}>이전 신청 확인</button>
            <button className="btn btn-blue ml10" onClick={handleList}>목록으로</button>
          </>
        );
      }
    }
  
    /* 신청마감·진행·종강 (B,C,D)  */
    if (pending) {
      // 마감·진행·종강 상태라도 승인 대기(A)면 취소 가능
      return (
        <>
          <button className="btn btn_red" onClick={handleCancelApply}>교육 신청 취소</button>
          <button className="btn btn_blue ml10" onClick={() => setIsDoneModal(true)}>교육 신청 확인</button>
          <button className="btn btn-blue ml10" onClick={handleList}>목록으로</button>
        </>
      );
    }
    // 나머진 신청 확인 + 목록만
    return (
      <>
        <button className="btn btn_blue" onClick={() => setIsDoneModal(true)}>교육 신청 확인</button>
        <button className="btn btn-blue ml10" onClick={handleList}>목록으로</button>
      </>
    );
  };



  return (
    <>
      <ScrollTop />
      <main id="main" className="mainpage">
        <div>
          <section className="section main-contact">
            {/* 헤더 네비게이션 */}
            <HeaderNavi selectedCategory={subMenu} />

            <div className="sub-container">
              <div className="sub-layout">
                {/* 왼쪽 메뉴 */}
                <MyPageLeftMenuStudent />

                {/* 본문 컨텐츠 */}
                <div className="sub-contact">
                  <div className="tit-T">{lectureNm}</div>
                  <div className="lineTop_tb01"> 
                      <table className="lineTop_tb">
                      <colgroup>
                          <col style={{ width: "20%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "18%" }} />
                          <col style={{ width: "7%" }} />
                          <col style={{ width: "9%" }} />
                          <col style={{ width: "10%" }} />
                          <col style={{ width: "9%" }} />
                      </colgroup>
                          <thead>
                              <tr>
                                  <th>교육분류</th>
                                  <th>교육유형</th>
                                  <th>신청기간</th>
                                  <th>교육일자</th>
                                  <th>교육시간</th>
                                  <th>정원수</th>
                                  <th>수강료</th>
                                  <th>교육상태</th>
                              </tr>
                          </thead>
                          <tbody>
                              <tr>
                                  <td>{lecture.eduCategoryNm}</td>
                                  <td>{lecture.eduTypeNm}</td>
                                  <td>{period}</td>
                                  <td>{lectureDate}</td>
                                  <td>{hoursText}</td>
                                  <td>{lecture.curStudentCnt}/{lecture.eduStdntLimit ?? ""}</td>
                                  <td>{feeText}</td>
                                  <td>{lecture.lectureStatusNm}</td>
                              </tr>
                          </tbody>
                      </table>
                  </div>
                  
                  <div className="view-txt">
                      <span>관련법령</span>
                      {lecture.eduLaw || '관련 법령 정보 없음'}
                      <span>교육목표</span>
                      {lecture.eduGoal}
                      <span>교육내용</span>
                      <div dangerouslySetInnerHTML={{ __html: lecture.eduContents }} />
                      <span>수료조건</span>
                      <p>출석(100%)</p>
                      <b className="txt-red small-font">* 정해진 교육시간을 모두 이수했을 경우에만 수료 할 수 있습니다.</b>
                      <span>교육장소</span>
                      <p>{addressText}</p>


                      <span>교육시간표</span>
                      {Object.keys(grouped).length ? (
                          <>
                          {Object.entries(grouped).map(([date, list]) =>
                              renderSchedule(date, list),
                          )}
                          </>
                      ) : null}

                        {/* <span>폐강기준</span>
                      교육생 인원이 10명 미만일 경우 자동으로 페강 처리 되며 교육일자 변경 또는 환불 됩니다  */}
                      <div className="box-border" style={{marginTop:"10px", marginBottom:"20px"}}>
                          <span className="txt-red">유의사항</span>
                          {/* <b className="txt-point-img"><img src="/img/btn_point.png" alt="포인트" />환불조건</b> 신청후 <b className="txt-blue">3일 유예기간</b> 까지 교육비 <b className="txt-blue">미결제 상태인 경우 신청</b> 취소 처리되며 <b className="txt-blue">신청 마감일 이후로부터~교육 당일 출석확인 전까지는 교육비
                          환불 불가</b> */}
                      </div>

                      <div className="text-cent">
                        {statusButtons()}
                      </div>
                  </div>
              </div>  
                {/* 본문 컨텐츠 끝 */}
              </div>
            </div>

            <Footer />
          </section>
        </div>
      </main>
      <ModalUser
          isOpen={isModalOpen}
          lecture={lecture}
          onClose={() => setIsModalOpen(false)}
          onOpenConfirm={handleApplyDone}
      />
      <ApplyCompleteModal
          isOpen={isDoneModal}
          onClose={() => setIsDoneModal(false)}
          lecture={lecture}
      />
    </>
  );
};

export default MyStudentLectureDetail;
